/**
 * Skill extraction — derive taxonomy skills from résumé text.
 *
 * ADR-025 rung 4: derive from our own data before asking the user. LinkedIn's
 * sidebar "Top Skills" carries only two or three entries and wraps across lines
 * in a narrow column, so depending on it produces a thin, brittle result.
 * Matching the text against the taxonomy is both more robust and more useful —
 * it yields skills with stable IDs, which is what every downstream screen needs.
 * Free-text skills would have to be resolved eventually anyway.
 */

// O*NET element names are formal ("Operations Analysis", "Systems Evaluation").
// Résumés use working language. These map the gap for the highest-value elements.
const SYNONYMS = {
  "Complex Problem Solving": ["problem solving", "troubleshooting", "root cause"],
  "Critical Thinking": ["critical thinking", "analysis", "analytical"],
  "Judgment and Decision Making": ["decision making", "prioritisation", "prioritization"],
  "Systems Analysis": ["systems analysis", "system design", "architecture"],
  "Systems Evaluation": ["systems evaluation", "performance tuning", "optimisation", "optimization"],
  "Operations Analysis": ["requirements", "operations analysis", "business analysis"],
  "Programming": ["programming", "coding", "software development", "java", "python", "javascript", "sql"],
  "Technology Design": ["solution design", "technical design", "platform design"],
  "Quality Control Analysis": ["quality assurance", "testing", "qa", "validation"],
  "Management of Personnel Resources": ["team leadership", "people management", "line management", "mentoring", "coaching"],
  "Management of Financial Resources": ["budget", "p&l", "cost management", "forecasting"],
  "Time Management": ["planning", "scheduling", "delivery management"],
  "Coordination": ["cross-functional", "stakeholder", "collaboration", "coordination"],
  "Persuasion": ["influencing", "persuasion", "buy-in"],
  "Negotiation": ["negotiation", "contract", "vendor management"],
  "Instructing": ["training", "enablement", "workshops"],
  "Social Perceptiveness": ["stakeholder management", "client relationships", "customer facing"],
  "Service Orientation": ["customer service", "customer success", "support"],
  "Speaking": ["presenting", "presentation", "public speaking"],
  "Writing": ["documentation", "technical writing", "reporting"],
  "Active Listening": ["requirements gathering", "discovery", "user research"],
  "Monitoring": ["monitoring", "kpi", "metrics", "reporting"],
  "Administration and Management": ["strategy", "governance", "operating model", "transformation"],
  "Customer and Personal Service": ["customer experience", "cx", "client service"],
  "Computers and Electronics": ["cloud", "infrastructure", "devops", "aws", "azure", "api", "database"],
  "Mathematics": ["statistics", "modelling", "modeling", "quantitative"],
  "Engineering and Technology": ["engineering", "integration", "automation"],
  "Personnel and Human Resources": ["recruiting", "hiring", "talent"],
  "Sales and Marketing": ["go-to-market", "marketing", "sales", "product marketing"],
  "Law and Government": ["compliance", "regulatory", "risk management", "audit"],
};

/**
 * @param {string} text            résumé text (role descriptions + summary)
 * @param {string[]} declared      skills the profile listed explicitly
 * @param {Array<{id,name,category}>} skills  the taxonomy dictionary
 */
/**
 * Punctuation and line breaks are word boundaries, not letters.
 *
 * The matcher below tests for ` term ` — space-delimited on both sides. Real
 * résumé text is "Agile methodologies, cloud computing, and DevOps." so almost
 * every phrase is terminated by a comma, a period or a newline, and almost every
 * match failed. One skill survived on a whole twenty-year technology profile,
 * because its synonym happened to be tested with the looser trailing-space form.
 * Normalise every non-word character to a space and the boundary test does what
 * it always meant to.
 */
const normalise = (s) => ` ${String(s || "").toLowerCase().replace(/[^a-z0-9+#&]+/g, " ").trim()} `;

export function extractSkills(text, declared, skills) {
  const hay = `${normalise(text)}${normalise((declared || []).join(" "))}`;
  const hits = new Map();

  const record = (skill, evidence, weight) => {
    const prev = hits.get(skill.id);
    if (!prev || weight > prev.weight) {
      hits.set(skill.id, { ...skill, evidence, weight });
    }
  };

  // Match at a word START, not between two spaces. A résumé says "stakeholders",
  // "presenting", "budgets" — the plural and the participle are the normal
  // forms, and a both-sides-space test rejects every one of them. The trailing
  // boundary is deliberately open: `stakeholder` should catch `stakeholders`,
  // and nothing in this vocabulary is a prefix of an unrelated word.
  const present = (term) => {
    const t = normalise(term).trim();
    return t ? hay.includes(` ${t}`) : false;
  };

  for (const skill of skills) {
    if (skill.category === "ai-signals-human") continue; // scored separately, not detected
    if (present(skill.name)) { record(skill, skill.name, 1); continue; }
    for (const syn of SYNONYMS[skill.name] || []) {
      if (present(syn)) { record(skill, syn, 0.7); break; }
    }
  }

  // Skills the person declared but we could not map stay visible — dropping them
  // silently would lose real signal and make the result look thinner than it is.
  const mapped = new Set([...hits.values()].map((h) => h.evidence.toLowerCase()));
  const unmapped = (declared || []).filter((d) => !mapped.has(d.toLowerCase()));

  return {
    matched: [...hits.values()].sort((a, b) => b.weight - a.weight),
    unmapped,
    coverage: hits.size,
  };
}
